/**
 * Policy New Page
 * Page for creating a new policy draft manually
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

import {
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { PolicyEditForm } from '@/components/policies/policy-edit-form'
import { useCreatePolicy } from '@/hooks/use-policies'
import { retrieveXacmlForPolicyCreation } from '@/lib/cross-app-context'
import type { CreatePolicyRequest, PolicyDraft } from '@/types/policy'

/**
 * Generate a unique policy name with random suffix
 */
function generatePolicyName(): string {
  const randomSuffix = Math.random().toString(16).slice(2, 6)
  return `policy-${randomSuffix}`
}

/**
 * Policy New Page Component
 */
export function PolicyNewPage() {
  const navigate = useNavigate()

  // Check for prefilled XACML from cross-app flow (e.g., Polix AI -> Create Policy)
  const [prefill] = useState<PolicyDraft | null>(() => {
    const xacmlJson = retrieveXacmlForPolicyCreation()
    if (!xacmlJson) return null

    try {
      const xacmlPolicy = JSON.parse(xacmlJson)
      return {
        id: '',
        tenantId: '',
        conversationId: null,
        name: xacmlPolicy.PolicyId ?? generatePolicyName(),
        description: 'Created from AI-generated XACML policy',
        status: 'DRAFT' as const,
        xacmlPolicy,
        humanReadable: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: '',
      }
    } catch {
      return null
    }
  })

  // Create mutation
  const createMutation = useCreatePolicy()

  // Handle form submission
  const handleSubmit = (data: { name?: string; description?: string; xacmlPolicy?: Record<string, unknown> }) => {
    const request: CreatePolicyRequest = {
      name: data.name || generatePolicyName(),
      description: data.description,
      xacmlPolicy: data.xacmlPolicy || {},
    }

    createMutation.mutate(request)
  }

  // Handle cancel
  const handleCancel = () => {
    navigate('/pac/policies')
  }

  return (
    <>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/pac/policies')}
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Separator orientation="vertical" className="mx-2 h-4" />
          <h1 className="text-base font-medium">New Policy</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        <Card>
          <CardHeader>
            <CardTitle>Create New Policy</CardTitle>
            <CardDescription>
              Define a new access control policy with name, description, and XACML content
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PolicyEditForm
              policy={prefill}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isSubmitting={createMutation.isPending}
              forceCreateMode
            />
          </CardContent>
        </Card>
      </div>
    </>
  )
}

export default PolicyNewPage
